import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Row, Spinner } from "react-bootstrap";
import { Copy, KeyRound, Pencil, RefreshCw, Trash2 } from "lucide-react";
import ApiKeyFormModal from "./ApiKeyFormModal";
import {
  ApiKey,
  ApiKeyAccessLog,
  ScopeInfo,
  deleteApiKey,
  fetchAllActivity,
  fetchApiKeyActivity,
  fetchApiKeys,
  fetchScopes,
  rotateApiKey,
} from "../../services/apiKeyService";

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function statusCodeClass(code: number) {
  if (code >= 200 && code < 300) return "ak-badge-status-ok";
  if (code >= 400 && code < 500) return "ak-badge-status-warn";
  return "ak-badge-status-err";
}

const ApiKeysSection: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [activity, setActivity] = useState<ApiKeyAccessLog[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [keyList, scopeList] = await Promise.all([fetchApiKeys(), fetchScopes()]);
      setKeys(keyList);
      setScopes(scopeList);
      if (keyList.length > 0 && selectedKeyId === null) {
        setSelectedKeyId(keyList[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [selectedKeyId]);

  const loadActivity = useCallback(async (keyId: number | null) => {
    setActivityLoading(true);
    try {
      const logs = keyId ? await fetchApiKeyActivity(keyId) : await fetchAllActivity();
      setActivity(logs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load activity");
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  useEffect(() => {
    loadActivity(selectedKeyId);
  }, [selectedKeyId, loadActivity]);

  const selectedKey = keys.find((k) => k.id === selectedKeyId) || null;

  const handleCreate = () => {
    setEditingKey(null);
    setShowForm(true);
  };

  const handleEdit = (key: ApiKey) => {
    setEditingKey(key);
    setShowForm(true);
  };

  const handleSaved = async (created?: { raw_key: string; id: number }) => {
    if (created?.raw_key) {
      setNewRawKey(created.raw_key);
      setSelectedKeyId(created.id);
    }
    await loadKeys();
  };

  const handleRotate = async (key: ApiKey) => {
    if (!window.confirm(`Rotate key "${key.name}"? The old key will stop working immediately.`)) {
      return;
    }
    try {
      const result = await rotateApiKey(key.id);
      setNewRawKey(result.raw_key);
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rotate key");
    }
  };

  const handleDelete = async (key: ApiKey) => {
    if (!window.confirm(`Delete key "${key.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteApiKey(key.id);
      if (selectedKeyId === key.id) {
        setSelectedKeyId(null);
      }
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete key");
    }
  };

  const copyRawKey = async () => {
    if (!newRawKey) return;
    await navigator.clipboard.writeText(newRawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: "#8ab4f8" }} />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" className="ak-alert ak-alert-danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {newRawKey && (
        <Alert className="ak-alert ak-alert-success mb-4">
          <div className="fw-semibold mb-2">New API key — copy it now</div>
          <p className="mb-2 small text-muted">
            This is the only time the full key will be shown. Email it with the connection guide.
          </p>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <code className="p-2 rounded flex-grow-1" style={{ wordBreak: "break-all" }}>
              {newRawKey}
            </code>
            <Button className="btn-ak-outline" size="sm" onClick={copyRawKey}>
              <Copy size={14} className="me-1" />
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button className="btn-ak-outline" size="sm" onClick={() => setNewRawKey(null)}>
              Dismiss
            </Button>
          </div>
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Manage Keys</h5>
          <p className="text-muted mb-0 small">
            Invite-only access. Create keys with the permissions each integration needs.
          </p>
        </div>
        <Button className="btn-ak-primary" onClick={handleCreate}>
          <KeyRound size={16} className="me-1" />
          Create Key
        </Button>
      </div>

      <Row className="g-3">
        <Col lg={5}>
          <div className="ak-card h-100">
            {keys.length === 0 ? (
              <div className="p-4 text-muted text-center">No API keys yet.</div>
            ) : (
              <div className="list-group list-group-flush">
                {keys.map((key) => (
                  <button
                    key={key.id}
                    type="button"
                    className={`list-group-item list-group-item-action text-start border-0 ${
                      selectedKeyId === key.id ? "active" : ""
                    }`}
                    onClick={() => setSelectedKeyId(key.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="fw-semibold">{key.name}</div>
                        <div className="small text-muted">{key.key_prefix}…</div>
                        {key.contact_email && (
                          <div className="small text-muted">{key.contact_email}</div>
                        )}
                      </div>
                      <span className={`ak-badge ${key.is_active ? "ak-badge-active" : "ak-badge-inactive"}`}>
                        {key.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Col>

        <Col lg={7}>
          {selectedKey ? (
            <div className="ak-card p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="mb-1">{selectedKey.name}</h5>
                  <div className="text-muted small">{selectedKey.description || "No description"}</div>
                </div>
                <div className="d-flex gap-2">
                  <Button className="btn-ak-outline" size="sm" onClick={() => handleEdit(selectedKey)}>
                    <Pencil size={14} />
                  </Button>
                  <Button className="btn-ak-outline" size="sm" onClick={() => handleRotate(selectedKey)}>
                    <RefreshCw size={14} />
                  </Button>
                  <Button className="btn-ak-danger" size="sm" onClick={() => handleDelete(selectedKey)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              <Row className="g-3 mb-3">
                <Col sm={6}>
                  <div className="text-muted small">Contact email</div>
                  <div>{selectedKey.contact_email || "—"}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Key prefix</div>
                  <code>{selectedKey.key_prefix}…</code>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Last used</div>
                  <div>{formatDate(selectedKey.last_used_at)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Created</div>
                  <div>{formatDate(selectedKey.created_at)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Expires</div>
                  <div>{formatDate(selectedKey.expires_at)}</div>
                </Col>
              </Row>

              <div className="mb-2 text-muted small">Permissions</div>
              <div className="d-flex flex-wrap gap-2 mb-4">
                {selectedKey.scopes.map((scopeId) => {
                  const scope = scopes.find((s) => s.id === scopeId);
                  return (
                    <span key={scopeId} className="ak-badge">
                      {scope?.label || scopeId}
                    </span>
                  );
                })}
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Recent API Activity</h6>
                <Button
                  className="btn-ak-outline"
                  size="sm"
                  onClick={() => loadActivity(selectedKeyId)}
                  disabled={activityLoading}
                >
                  <RefreshCw size={14} className={activityLoading ? "spin" : ""} />
                </Button>
              </div>

              {activityLoading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" animation="border" style={{ color: "#8ab4f8" }} />
                </div>
              ) : activity.length === 0 ? (
                <div className="text-muted small">No API calls recorded yet for this key.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Method</th>
                        <th>Path</th>
                        <th>Status</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((log) => (
                        <tr key={log.id}>
                          <td className="small text-nowrap">{formatDate(log.created_at)}</td>
                          <td className="small">{log.method}</td>
                          <td className="small text-truncate" style={{ maxWidth: 220 }}>
                            {log.path}
                          </td>
                          <td className={`small ${statusCodeClass(log.status_code)}`}>
                            {log.status_code}
                          </td>
                          <td className="small text-muted">
                            {log.duration_ms != null ? `${log.duration_ms}ms` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="ak-card p-5 text-muted text-center">
              Select an API key to view details and activity.
            </div>
          )}
        </Col>
      </Row>

      <ApiKeyFormModal
        show={showForm}
        onHide={() => setShowForm(false)}
        scopes={scopes}
        editingKey={editingKey}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default ApiKeysSection;
